/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, Building2, Send, Copy, Pencil, Archive, ArchiveRestore, Trash2, RefreshCw, Users, Link2 as LinkIcon, Unlink2 as UnlinkIcon } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import { clientDisplayName } from "@/lib/utils";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import PracticeManagerLink from "@/components/PracticeManagerLink";
import PortalFeaturesCard from "@/components/PortalFeaturesCard";
import AddOnsCard from "@/components/AddOnsCard";
import ExcelFinancialsCard from "@/components/ExcelFinancialsCard";
import EditableEmailPreview from "@/components/EditableEmailPreview";
import MonthlySalesReportSender from "@/components/MonthlySalesReportSender";
import TaxGstSettingsCard from "@/components/TaxGstSettingsCard";
import ClientNotesTab from "@/components/ClientNotesTab";
import { DOC_TYPES, ENTITY_TYPES, labelFor, relativeTime, formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function FirmClientDetail() {
    const { tenant, user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const nav = useNavigate();
    const { clientId } = useParams();
    const [client, setClient] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [requests, setRequests] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [compliance, setCompliance] = useState([]);
    const [readiness, setReadiness] = useState(null);
    const [workItems, setWorkItems] = useState([]);

    async function load() {
        try {
            const [c, t, r, s, cp, rd, wi] = await Promise.all([
                api.get(`/clients/${clientId}`),
                api.get(`/clients/${clientId}/timeline`),
                api.get(`/requests/`, { params: { client_id: clientId } }),
                api.get(`/documents/`, { params: { client_id: clientId } }),
                api.get(`/compliance/`, { params: { client_id: clientId } }),
                api.get(`/work-queue/readiness/${clientId}`).catch(() => ({ data: null })),
                api.get(`/work-queue/`, { params: { client_id: clientId } }).catch(() => ({ data: [] })),
            ]);
            setClient(c.data);
            setTimeline(t.data);
            setRequests(r.data);
            setSubmissions(s.data);
            setCompliance(cp.data);
            setReadiness(rd.data);
            setWorkItems(wi.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => { load(); /* eslint-disable-line */ }, [clientId]);

    if (!client) return <div className="text-sm text-[#5E636E]">Loading…</div>;

    return (
        <div>
            <div className="flex items-center gap-3 flex-wrap">
                <Link to="/app/clients" className="inline-flex items-center gap-1.5 text-xs text-[#5E636E] hover:text-[#0F1115]" data-testid="client-back-link">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to clients
                </Link>
                {client.external_source === "practice_manager" && (
                    <PracticeManagerLink
                        url={tenant?.practice_manager_url}
                        label={tenant?.practice_manager_label}
                        entityId={client.external_id}
                        entityKind="client"
                        size="sm"
                        testId="client-detail-pm-link"
                    />
                )}
            </div>
            <PageHeader
                eyebrow={client.archived_at ? "Client · ARCHIVED" : "Client"}
                title={clientDisplayName(client)}
                description={
                    [
                        client.alias ? `Contact name · ${client.alias}` : null,
                        [client.contact_email, client.contact_phone].filter(Boolean).join(" · ") || null,
                    ].filter(Boolean).join(" — ")
                }
                actions={
                    <div className="flex gap-2 flex-wrap justify-end max-w-[520px]">
                        <ClientGroupPicker client={client} onChanged={load} />
                        <InviteButton client={client} onChanged={load} />
                        <EntityDialog clientId={clientId} country={"NZ"} onCreated={load} />
                        <DangerZone
                            client={client}
                            isAdmin={isAdmin}
                            onArchived={load}
                            onDeleted={() => nav("/app/clients", { replace: true })}
                        />
                    </div>
                }
                testId="client-detail-header"
            >
            </PageHeader>

            <Tabs defaultValue="overview" className="mt-2">
                <TabsList data-testid="client-tabs">
                    <TabsTrigger value="overview" data-testid="client-tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="entities" data-testid="client-tab-entities">Entities ({client.entities?.length || 0})</TabsTrigger>
                    <TabsTrigger value="requests" data-testid="client-tab-requests">Requests ({requests.length})</TabsTrigger>
                    <TabsTrigger value="submissions" data-testid="client-tab-submissions">Submissions ({submissions.length})</TabsTrigger>
                    <TabsTrigger value="compliance" data-testid="client-tab-compliance">Compliance ({compliance.length})</TabsTrigger>
                    <TabsTrigger value="notes" data-testid="client-tab-notes">Notes</TabsTrigger>
                    <TabsTrigger value="timeline" data-testid="client-tab-timeline">Timeline</TabsTrigger>
                    <TabsTrigger value="reports" data-testid="client-tab-reports">Reports</TabsTrigger>
                    <TabsTrigger value="portal" data-testid="client-tab-portal">Portal</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <ReadinessCard readiness={readiness} workItems={workItems} requests={requests} submissions={submissions} />
                        <div className="co-card co-card-pad">
                            <div className="flex items-start justify-between gap-3">
                                <div className="co-label">Contact</div>
                                <EditContactButton client={client} onSaved={load} />
                            </div>
                            <dl className="mt-3 grid grid-cols-2 gap-y-2 text-sm">
                                <dt className="text-[#5E636E]">Name</dt><dd data-testid="client-contact-name">{client.name}</dd>
                                <dt className="text-[#5E636E]">Alias</dt><dd data-testid="client-contact-alias">{client.alias || <span className="text-[#9CA0A8]">—</span>}</dd>
                                <dt className="text-[#5E636E]">Email</dt><dd data-testid="client-contact-email">{client.contact_email || "—"}</dd>
                                <dt className="text-[#5E636E]">Phone</dt><dd data-testid="client-contact-phone">{client.contact_phone || "—"}</dd>
                                <dt className="text-[#5E636E]">Portal user</dt><dd>{client.primary_user_id ? "Linked" : "Not invited"}</dd>
                            </dl>
                            {client.notes && (
                                <>
                                    <div className="co-label mt-5">Internal notes</div>
                                    <p className="mt-2 text-sm text-[#5E636E]">{client.notes}</p>
                                </>
                            )}
                        </div>
                        <div className="co-card co-card-pad">
                            <div className="co-label">Counts</div>
                            <div className="mt-3 grid grid-cols-3 gap-3">
                                <Stat n={client.entities?.length || 0} l="Entities" />
                                <Stat n={requests.filter(r => r.status !== "completed" && r.status !== "cancelled").length} l="Open requests" />
                                <Stat n={submissions.filter(s => s.status === "pending_review").length} l="Pending review" />
                            </div>
                        </div>
                        <div className="co-card co-card-pad lg:col-span-2">
                            <ReviewToggle client={client} onChanged={load} />
                        </div>
                        <div className="co-card co-card-pad lg:col-span-2 bg-[#F5F4EF] border-dashed">
                            <div className="text-xs text-[#5E636E]">
                                Add-ons (Cash Register, Morning Brief, Spend Insights, Job Odometer) and per-tab visibility toggles moved to the <span className="font-medium text-[#0F1115]">Portal</span> tab above.
                            </div>
                        </div>
                        <div className="co-card co-card-pad lg:col-span-2">
                            <DataSourceCard client={client} onChanged={load} />
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="entities" className="mt-4">
                    {client.entities?.length === 0 ? (
                        <div className="text-sm text-[#5E636E]">No entities yet — add one to capture company / sole trader details.</div>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {client.entities.map((e) => (
                                <div key={e.id} className="co-card co-card-pad" data-testid={`entity-${e.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="font-display text-lg font-medium">{e.name}</div>
                                            <div className="text-xs text-[#5E636E] mt-1">{labelFor(ENTITY_TYPES, e.entity_type)} · {e.country}</div>
                                        </div>
                                        <Building2 className="h-4 w-4 text-[#5E636E]" />
                                    </div>
                                    {e.tax_number && <div className="mt-3 text-xs font-mono text-[#5E636E]">{e.tax_number}</div>}
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="requests" className="mt-4">
                    <div className="co-card overflow-hidden">
                        <ul className="divide-y divide-[#E4E4E7]" data-testid="client-requests-list">
                            {requests.length === 0 && <li className="p-4 text-sm text-[#5E636E]">No requests sent.</li>}
                            {requests.map((r) => (
                                <li key={r.id} className="p-4 flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{r.title}</div>
                                        <div className="text-xs text-[#5E636E] mt-0.5 line-clamp-2">{r.description}</div>
                                        <div className="text-[11px] text-[#5E636E] mt-1">Due {formatDate(r.due_date)}</div>
                                    </div>
                                    <StatusBadge status={r.status} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </TabsContent>

                <TabsContent value="submissions" className="mt-4">
                    <div className="co-card overflow-hidden">
                        <ul className="divide-y divide-[#E4E4E7]" data-testid="client-submissions-list">
                            {submissions.length === 0 && <li className="p-4 text-sm text-[#5E636E]">No documents uploaded yet.</li>}
                            {submissions.map((s) => (
                                <li key={s.id} className="p-4 flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">{labelFor(DOC_TYPES, s.document_type)}</div>
                                        <div className="text-xs text-[#5E636E] mt-0.5">
                                            {s.files?.length || 0} file{(s.files?.length || 0) === 1 ? "" : "s"} · {s.period_start || ""} → {s.period_end || ""}
                                        </div>
                                        <div className="text-[11px] text-[#5E636E] mt-1">{relativeTime(s.created_at)}</div>
                                    </div>
                                    <StatusBadge status={s.status} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </TabsContent>

                <TabsContent value="timeline" className="mt-4">
                    <div className="co-card co-card-pad">
                        <ul className="space-y-3" data-testid="client-timeline">
                            {timeline.length === 0 && <li className="text-sm text-[#5E636E]">No activity yet.</li>}
                            {timeline.map((a) => (
                                <li key={a.id} className="flex items-start gap-3">
                                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#5E636E]" />
                                    <div>
                                        <div className="text-sm"><span className="font-medium">{a.actor_name || "System"}</span> <span className="text-[#5E636E]">{a.action.replace(/_/g, " ")}</span></div>
                                        <div className="text-[11px] text-[#5E636E]">{relativeTime(a.created_at)}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </TabsContent>

                <TabsContent value="compliance" className="mt-4">
                    <ComplianceTab clientId={clientId} compliance={compliance} onChanged={load} />
                </TabsContent>

                <TabsContent value="notes" className="mt-4">
                    <ClientNotesTab clientId={clientId} />
                </TabsContent>

                <TabsContent value="reports" className="mt-4">
                    <MonthlySalesReportSender clientId={clientId} />
                </TabsContent>

                <TabsContent value="portal" className="mt-4">
                    <div className="space-y-4">
                        <TaxGstSettingsCard client={client} onSaved={load} />
                        <PortalFeaturesCard clientId={clientId} clientName={client?.name} />
                        <AddOnsCard client={client} onChanged={load} />
                        <ExcelFinancialsCard client={client} onUploaded={load} />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ n, l }) {
    return (
        <div>
            <div className="font-display text-2xl font-medium">{n}</div>
            <div className="text-[11px] text-[#5E636E]">{l}</div>
        </div>
    );
}

function ClientGroupPicker({ client, onChanged }) {
    const [groups, setGroups] = useState([]);
    useEffect(() => {
        api.get("/client-groups").then(({ data }) => setGroups(data.rows || [])).catch(() => {});
    }, []);
    const active = groups.find((g) => g.id === client.group_id);
    async function assign(val) {
        try {
            const gid = val === "none" ? "none" : val;
            await api.post(`/client-groups/${gid}/assign`, { client_ids: [client.id] });
            onChanged?.();
            toast.success(val === "none" ? "Group cleared" : `Assigned to ${groups.find((g) => g.id === val)?.name}`);
        } catch (e) { toast.error(formatApiError(e)); }
    }
    return (
        <Select value={client.group_id || "none"} onValueChange={assign}>
            <SelectTrigger className="h-8 text-xs bg-white gap-1.5" data-testid="client-group-picker">
                {active ? (
                    <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 rounded-full" style={{ background: active.color }} />{active.name}</span>
                ) : (
                    <span className="text-[#5E636E]">Set value tier…</span>
                )}
            </SelectTrigger>
            <SelectContent>
                {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
                <SelectItem value="none">Clear</SelectItem>
            </SelectContent>
        </Select>
    );
}

function InviteButton({ client, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [mode, setMode] = useState("send");  // "send" | "resend"
    const [pendingInvite, setPendingInvite] = useState(null);
    // When this client shares its contact_email with sibling entities in the
    // firm, we surface a hint so staff can send ONE group invite instead of
    // several one-off invites — the resulting portal user sees all entities
    // in the top-right switcher.
    const [siblings, setSiblings] = useState([]);
    const editorRef = useRef(null);

    useEffect(() => {
        if (client.primary_user_id) { setPendingInvite(null); return; }
        api.get("/invites/", { params: { client_id: client.id, status: "pending" } })
            .then(({ data }) => setPendingInvite(data[0] || null))
            .catch(() => {});
    }, [client.id, client.primary_user_id]);

    useEffect(() => {
        if (!client.contact_email) { setSiblings([]); return; }
        api.get("/invites/grouped-owners")
            .then(({ data }) => {
                const grp = (data || []).find((g) => g.email === client.contact_email.toLowerCase());
                if (!grp) { setSiblings([]); return; }
                setSiblings(grp.clients.filter((c) => c.id !== client.id).map((c) => ({
                    ...c,
                    all_linked: grp.all_linked,
                    already_grouped: !!grp.existing_user,
                })));
            })
            .catch(() => setSiblings([]));
    }, [client.id, client.contact_email]);

    async function copy(url) {
        try { await navigator.clipboard.writeText(url); toast.success("Activation link copied"); }
        catch (e) { window.prompt("Copy this activation link:", url); }
    }

    async function loadPreview(nextMode) {
        setBusy(true);
        try {
            const { data } = await api.post("/invites/preview", { client_id: client.id });
            setPreview(data);
            setMode(nextMode);
            setPreviewOpen(true);
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    async function confirmSend() {
        setBusy(true);
        try {
            const { data } = await api.post("/invites/", { client_id: client.id });
            setPendingInvite(data.invite);
            setPreviewOpen(false);
            await copy(data.invite.activation_url);
            toast.success("Invite sent — activation link copied to clipboard too");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    async function confirmResend() {
        if (!pendingInvite) return;
        setBusy(true);
        try {
            const edited = editorRef.current?.getValues();
            const body = {};
            if (edited?.subject && edited.subject !== preview?.subject) body.subject = edited.subject;
            if (edited?.html && edited.html !== preview?.html) body.html = edited.html;
            const { data } = await api.post(`/invites/${pendingInvite.id}/resend`, body);
            setPendingInvite(data.invite);
            setPreviewOpen(false);
            await copy(data.invite.activation_url);
            const editedNote = (body.subject || body.html) ? " (with your edits)" : "";
            toast.success(`Invite email resent${editedNote} — fresh link copied to your clipboard.`);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    if (client.primary_user_id) {
        return (
            <div className="inline-flex flex-col items-end gap-1">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                    <Send className="h-3.5 w-3.5" /> Portal active
                </span>
                <UnlinkPortalUserButton client={client} onDone={onChanged} />
                {siblings.length > 0 && !siblings[0]?.all_linked && (
                    <SiblingHint client={client} siblings={siblings} onDone={onChanged} alreadyPortalUser />
                )}
                <GrantAccessButton client={client} onDone={onChanged} />
            </div>
        );
    }

    const isResend = mode === "resend";

    return (
        <>
            {siblings.length > 0 && (
                <div className="mb-2 w-full">
                    <SiblingHint client={client} siblings={siblings} onDone={onChanged} />
                </div>
            )}
            <div className="mb-2 w-full">
                <GrantAccessButton client={client} onDone={onChanged} />
            </div>
            {pendingInvite ? (
                <div className="inline-flex flex-wrap items-center gap-2">
                    <Button variant="outline" onClick={() => copy(pendingInvite.activation_url)} data-testid="copy-pending-invite-btn">
                        <Copy className="h-4 w-4" /> Copy invite link
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => loadPreview("resend")}
                        disabled={busy}
                        title="Preview the same activation email before resending"
                        data-testid="resend-invite-btn"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4" /> Resend email</>}
                    </Button>
                </div>
            ) : (
                <Button
                    onClick={() => loadPreview("send")}
                    disabled={busy || !client.contact_email}
                    className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                    data-testid="invite-client-btn"
                >
                    {busy && !previewOpen ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Invite to portal</>}
                </Button>
            )}
            <Dialog open={previewOpen} onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreview(null); }}>
                <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="invite-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>{isResend ? "Edit &amp; resend invite email" : "Preview invite email"}</DialogTitle>
                        <DialogDescription>
                            {isResend
                                ? `Edit any wording inline before sending to ${client.contact_email || "the client"}. A fresh 30-day activation link is minted when you hit Resend.`
                                : `This is exactly what ${client.contact_email || "the client"} will receive — including the iPhone + Android home-screen setup steps.`}
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (isResend ? (
                        <EditableEmailPreview
                            ref={editorRef}
                            initialSubject={preview.subject}
                            initialHtml={preview.html}
                            toEmail={preview.to || client.contact_email}
                        />
                    ) : (
                        <div className="flex-1 overflow-hidden flex flex-col gap-2 min-h-0">
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                                <span className="text-[#5E636E]">To</span><span className="font-mono truncate">{preview.to || client.contact_email || "—"}</span>
                                <span className="text-[#5E636E]">Subject</span><span className="font-medium truncate">{preview.subject}</span>
                            </div>
                            <iframe
                                title="Invite email preview"
                                srcDoc={preview.html}
                                sandbox=""
                                className="flex-1 min-h-[420px] w-full rounded-md border border-[#E4E4E7] bg-white"
                                data-testid="invite-preview-iframe"
                            />
                            <div className="text-[10px] text-[#5E636E]">
                                Activation link in preview shown as <code className="text-[#111]">/activate/PREVIEW-EXAMPLE-TOKEN</code> — the real single-use token is minted when you hit Send.
                            </div>
                        </div>
                    ))}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={busy}>Cancel</Button>
                        <Button
                            onClick={isResend ? confirmResend : confirmSend}
                            disabled={busy || !client.contact_email}
                            className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                            data-testid={isResend ? "invite-resend-confirm-btn" : "invite-send-btn"}
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isResend ? (<><RefreshCw className="h-4 w-4" /> Resend email</>) : (<><Send className="h-4 w-4" /> Send invite</>)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function SiblingHint({ client, siblings, onDone, alreadyPortalUser }) {
    const [busy, setBusy] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const editorRef = useRef(null);

    async function primaryAction() {
        // "Link extras" (existing portal user) → no email sent, just link IDs.
        if (alreadyPortalUser) {
            await sendGroup();
            return;
        }
        // "Invite as one owner" → show editable email preview first.
        setBusy(true);
        try {
            const ids = [client.id, ...siblings.map((s) => s.id)];
            const { data } = await api.post("/invites/preview", {
                client_ids: ids,
                email: client.contact_email,
                name: client.name,
            });
            setPreview(data);
            setPreviewOpen(true);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function sendGroup() {
        setBusy(true);
        try {
            const ids = [client.id, ...siblings.map((s) => s.id)];
            const body = {
                email: client.contact_email,
                client_ids: ids,
                name: client.name,
            };
            if (previewOpen) {
                const edited = editorRef.current?.getValues();
                if (edited?.subject && edited.subject !== preview?.subject) body.subject = edited.subject;
                if (edited?.html && edited.html !== preview?.html) body.html = edited.html;
            }
            const { data } = await api.post("/invites/group", body);
            if (data.status === "appended_to_existing_user") {
                toast.success(`Linked ${siblings.length} more entities to the existing portal login for ${client.contact_email}`);
            } else if (data?.invite?.token) {
                const editedNote = (body.subject || body.html) ? " (with your edits)" : "";
                const url = `${window.location.origin}/activate/${data.invite.token}`;
                try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
                toast.success(`Group invite sent${editedNote} — link copied. Covers ${ids.length} entities.`);
            } else {
                toast.success("Group invite updated");
            }
            setPreviewOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }
    return (
        <>
        <div
            className="rounded-md border border-sky-200 bg-sky-50/70 px-3 py-2 text-[12px] text-sky-900 flex items-start gap-2"
            data-testid="sibling-hint"
        >
            <Users className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1">
                <div className="font-medium">
                    {alreadyPortalUser
                        ? `This client's email owns ${siblings.length + 1} entities in your firm.`
                        : `This email also owns ${siblings.length} other ${siblings.length === 1 ? "entity" : "entities"} in your firm.`}
                </div>
                <div className="mt-0.5 text-[11px] text-sky-800/80">
                    {siblings.map((s) => s.name).join(" · ")}
                </div>
                <div className="mt-1 text-[11px] text-sky-800/80">
                    {alreadyPortalUser
                        ? "Add the extras to the same portal login so they can switch between entities in one place."
                        : "Send ONE portal invite that covers all of them — the client picks the entity from the top-right switcher after login."}
                </div>
            </div>
            <Button
                size="sm"
                variant="outline"
                onClick={primaryAction}
                disabled={busy || !client.contact_email}
                className="shrink-0"
                data-testid="sibling-group-invite-btn"
            >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (alreadyPortalUser ? "Link extras" : "Invite as one owner")}
            </Button>
        </div>

        {/* Editable preview dialog (only shown for the "Invite as one owner" path) */}
        <Dialog open={previewOpen} onOpenChange={(o) => { setPreviewOpen(o); if (!o) setPreview(null); }}>
            <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="sibling-group-preview-dialog">
                <DialogHeader>
                    <DialogTitle>Edit &amp; send group invite</DialogTitle>
                    <DialogDescription>
                        This one email covers {siblings.length + 1} entities: {[client.name, ...siblings.map((s) => s.name)].join(", ")}. Tweak the wording inline before sending to {client.contact_email}.
                    </DialogDescription>
                </DialogHeader>
                {preview && (
                    <EditableEmailPreview
                        ref={editorRef}
                        initialSubject={preview.subject}
                        initialHtml={preview.html}
                        toEmail={preview.to || client.contact_email}
                    />
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => setPreviewOpen(false)} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={sendGroup}
                        disabled={busy}
                        className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                        data-testid="sibling-group-send-confirm"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send group invite</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}


function GrantAccessButton({ client, onDone }) {
    const [open, setOpen] = useState(false);
    const [portalUsers, setPortalUsers] = useState([]);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);

    async function loadUsers() {
        setLoading(true);
        try {
            const { data } = await api.get("/auth/users");
            // Only client-role users, exclude anyone who already has access to THIS entity
            const rows = (data || []).filter((u) => u.role === "client" && u.email && !(u.client_id === client.id) && !(u.client_ids || []).includes(client.id));
            // Enrich with names of entities they already own so firm can pick confidently
            const idsPerUser = new Map(rows.map((u) => [u.id, [
                ...(u.client_id ? [u.client_id] : []),
                ...(u.client_ids || []).filter((i) => i !== u.client_id),
            ]]));
            const allNeededIds = Array.from(new Set([].concat(...idsPerUser.values())));
            let namesById = {};
            if (allNeededIds.length) {
                try {
                    const cl = await api.get("/clients/", { params: { include_archived: true } });
                    const map = {};
                    for (const c of cl.data || []) map[c.id] = c.name;
                    namesById = map;
                } catch { /* ignore */ }
            }
            setPortalUsers(rows.map((u) => ({
                ...u,
                entity_names: (idsPerUser.get(u.id) || []).map((i) => namesById[i]).filter(Boolean),
            })));
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    async function grantTo(user) {
        setBusy(true);
        try {
            const existingIds = [
                ...(user.client_id ? [user.client_id] : []),
                ...(user.client_ids || []).filter((i) => i !== user.client_id),
            ];
            const nextIds = Array.from(new Set([...existingIds, client.id]));
            const { data } = await api.post("/invites/group", {
                email: user.email,
                client_ids: nextIds,
                name: user.name,
            });
            if (data.status === "appended_to_existing_user") {
                toast.success(`${client.name} now accessible via ${user.email} — they can switch to it from the portal switcher.`);
            } else {
                toast.success(`Portal access granted to ${user.email}`);
            }
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const filtered = portalUsers.filter((u) => {
        const s = q.toLowerCase().trim();
        if (!s) return true;
        return u.email.toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s) || u.entity_names.some((n) => n.toLowerCase().includes(s));
    });

    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => { setOpen(true); loadUsers(); }}
                className="text-[#5E636E] hover:text-[#0F1115] text-xs"
                data-testid="grant-access-btn"
            >
                <LinkIcon className="h-3.5 w-3.5" /> Also let another portal user access this
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="grant-access-dialog">
                    <DialogHeader>
                        <DialogTitle>Grant portal access to {client.name}</DialogTitle>
                        <DialogDescription>
                            Pick an existing portal user in your firm. They&apos;ll be able to switch to this
                            entity from the portal switcher — no new email, no new password. Useful when a
                            client owner uses one login to view multiple entities each with their own contact email.
                        </DialogDescription>
                    </DialogHeader>
                    <Input
                        placeholder="Search by email, name, or entity…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="grant-access-search"
                    />
                    <div className="max-h-[360px] overflow-auto divide-y divide-[#E4E4E7] rounded-md border border-[#E4E4E7] mt-2">
                        {loading && <div className="p-4 text-sm text-[#5E636E] flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
                        {!loading && filtered.length === 0 && (
                            <div className="p-4 text-sm text-[#5E636E]">
                                No portal users match. Either invite a new one via the button above, or add one from the client whose owner should get access to this entity.
                            </div>
                        )}
                        {!loading && filtered.map((u) => (
                            <div key={u.id} className="p-3 flex items-center gap-3" data-testid={`grant-access-row-${u.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{u.name || u.email}</div>
                                    <div className="text-[11px] font-mono text-[#5E636E] truncate">{u.email}</div>
                                    {u.entity_names.length > 0 && (
                                        <div className="mt-0.5 text-[11px] text-[#5E636E] truncate">
                                            Currently accesses: {u.entity_names.slice(0, 3).join(", ")}{u.entity_names.length > 3 ? `, +${u.entity_names.length - 3}` : ""}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    size="sm"
                                    onClick={() => grantTo(u)}
                                    disabled={busy}
                                    className="shrink-0 bg-[#0E3B2E] hover:bg-[#0B2F25] text-white"
                                    data-testid={`grant-access-btn-${u.id}`}
                                >
                                    Grant
                                </Button>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function UnlinkPortalUserButton({ client, onDone }) {
    const [open, setOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(false);

    async function loadLinkedUser() {
        setLoading(true);
        try {
            const { data } = await api.get("/auth/users");
            const match = (data || []).find(
                (u) =>
                    u.id === client.primary_user_id ||
                    (u.role === "client" && Array.isArray(u.client_ids) && u.client_ids.includes(client.id)),
            );
            setUser(match || null);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    async function unlink() {
        if (!user) return;
        setBusy(true);
        try {
            const { data } = await api.post("/invites/unlink", {
                user_id: user.id,
                client_id: client.id,
            });
            const remaining = (data.remaining_client_ids || []).length;
            toast.success(
                `Unlinked ${client.name} from ${data.user_email}. They still have access to ${remaining} other ${remaining === 1 ? "entity" : "entities"}.`,
            );
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const linkedCount = user ? ((user.client_ids || []).length || 1) : null;
    const isLastEntity = linkedCount === 1;

    return (
        <>
            <Button
                variant="ghost"
                size="sm"
                onClick={() => { setOpen(true); loadLinkedUser(); }}
                className="text-[#5E636E] hover:text-[#B91C1C] text-xs"
                data-testid="unlink-portal-user-btn"
            >
                <UnlinkIcon className="h-3.5 w-3.5" /> Unlink this entity from portal login
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="unlink-portal-user-dialog">
                    <DialogHeader>
                        <DialogTitle>Unlink {client.name} from portal login</DialogTitle>
                        <DialogDescription>
                            The portal user keeps their login and any other entities they manage. Only this
                            one entity stops showing up in their entity switcher.
                        </DialogDescription>
                    </DialogHeader>
                    {loading ? (
                        <div className="flex items-center gap-2 text-sm text-[#5E636E] py-6 justify-center">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading portal user…
                        </div>
                    ) : !user ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="unlink-no-user">
                            No portal user is currently linked to this entity — nothing to unlink.
                        </div>
                    ) : (
                        <div className="rounded-md border border-[#E4E4E7] bg-[#F8F8F5] px-3 py-2.5 text-sm" data-testid="unlink-user-summary">
                            <div className="font-medium">{user.name || user.email}</div>
                            <div className="text-[11px] font-mono text-[#5E636E]">{user.email}</div>
                            <div className="mt-1 text-[11px] text-[#5E636E]">
                                Currently has access to <strong>{linkedCount}</strong> {linkedCount === 1 ? "entity" : "entities"} in your firm.
                            </div>
                            {isLastEntity && (
                                <div className="mt-2 rounded-sm bg-rose-50 border border-rose-200 px-2 py-1.5 text-[11px] text-rose-900" data-testid="unlink-last-entity-warning">
                                    This is the ONLY entity linked to that login. You can&apos;t unlink the last one — delete the portal user instead if they no longer need access.
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                        <Button
                            onClick={unlink}
                            disabled={busy || !user || isLastEntity}
                            className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
                            data-testid="unlink-portal-user-confirm-btn"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UnlinkIcon className="h-4 w-4" /> Unlink</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}



function EditContactButton({ client, onSaved }) {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", alias: "", contact_email: "", contact_phone: "", notes: "", portal_segment: "" });
    const [busy, setBusy] = useState(false);
    const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => {
        setForm({
            name: client.name || "",
            alias: client.alias || "",
            contact_email: client.contact_email || "",
            contact_phone: client.contact_phone || "",
            notes: client.notes || "",
            portal_segment: client.portal_segment || "",
        });
    }, [client.id, client.name, client.alias, client.contact_email, client.contact_phone, client.notes, client.portal_segment]);

    const isPMSynced = client.external_source === "practice_manager";

    async function save() {
        setBusy(true);
        try {
            await api.patch(`/clients/${client.id}`, {
                name: form.name.trim() || undefined,
                alias: form.alias.trim() || null,
                contact_email: form.contact_email.trim() || null,
                contact_phone: form.contact_phone.trim() || null,
                notes: form.notes,
                portal_segment: form.portal_segment || null,
            });
            toast.success("Contact updated");
            setOpen(false);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[11px] text-[#5E636E] hover:text-[#0F1115]"
                    data-testid="client-edit-contact-btn"
                >
                    <Pencil className="h-3 w-3" /> Edit
                </button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Edit contact — {clientDisplayName(client)}</DialogTitle>
                </DialogHeader>
                {isPMSynced && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        This client is synced from Practice Manager. Local edits here will be overwritten on the next PM sync — update the source in Practice Manager for permanent changes. Use this for quick fixes (e.g. correcting a routing email for email intake).
                    </div>
                )}
                <div className="grid gap-3">
                    <div>
                        <Label>Name</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => s("name", e.target.value)}
                            data-testid="client-edit-name"
                        />
                    </div>
                    <div>
                        <Label>Contact name (alias) <span className="text-[#9CA0A8] font-normal">(optional)</span></Label>
                        <Input
                            value={form.alias}
                            onChange={(e) => s("alias", e.target.value)}
                            placeholder="e.g. Johny"
                            data-testid="client-edit-alias"
                        />
                        <p className="mt-1 text-[11px] text-[#5E636E]">
                            The first-name / friendly form used only for email salutations (e.g. "Hello Johny"). The client heading and lists always show the legal name.
                        </p>
                    </div>
                    <div>
                        <Label>Contact email</Label>
                        <Input
                            type="email"
                            value={form.contact_email}
                            onChange={(e) => s("contact_email", e.target.value)}
                            placeholder="e.g. owner@business.co.nz"
                            data-testid="client-edit-email"
                        />
                        <p className="mt-1 text-[11px] text-[#5E636E]">
                            Emails to <code className="font-mono">intake@upload.nztaxation.nz</code> from this address will auto-route to this client.
                        </p>
                    </div>
                    <div>
                        <Label>Contact phone</Label>
                        <Input
                            value={form.contact_phone}
                            onChange={(e) => s("contact_phone", e.target.value)}
                            placeholder="+64 21 555 1234"
                            data-testid="client-edit-phone"
                        />
                    </div>
                    <div>
                        <Label>Portal upload checklist</Label>
                        <Select value={form.portal_segment || "auto"} onValueChange={(v) => s("portal_segment", v === "auto" ? "" : v)}>
                            <SelectTrigger className="mt-1" data-testid="client-edit-portal-segment">
                                <SelectValue placeholder="Auto (based on Practice Manager)" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="auto">Auto (based on Practice Manager)</SelectItem>
                                <SelectItem value="small_company">Small Company</SelectItem>
                                <SelectItem value="sole_trader">Sole Trader</SelectItem>
                                <SelectItem value="rental">Rental / Airbnb owner</SelectItem>
                                <SelectItem value="rideshare">Uber / Rideshare driver</SelectItem>
                            </SelectContent>
                        </Select>
                        <p className="mt-1 text-[11px] text-[#5E636E]">
                            Controls the tailored document checklist this client sees on their portal Upload page. Leave on Auto to derive from Practice Manager customer type.
                        </p>
                    </div>
                    <div>
                        <Label>Internal notes</Label>
                        <textarea
                            value={form.notes}
                            onChange={(e) => s("notes", e.target.value)}
                            rows={3}
                            className="mt-1 w-full rounded-md border border-[#E4E4E7] bg-white px-3 py-2 text-sm"
                            data-testid="client-edit-notes"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        onClick={save}
                        disabled={busy}
                        className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                        data-testid="client-edit-save"
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function EntityDialog({ clientId, country, onCreated }) {    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", entity_type: "company", tax_number: "", country: country || "NZ" });
    const [busy, setBusy] = useState(false);
    const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    async function submit() {
        if (!form.name) return;
        setBusy(true);
        try {
            await api.post(`/clients/${clientId}/entities`, { client_id: clientId, ...form });
            toast.success("Entity added");
            setOpen(false);
            setForm({ name: "", entity_type: "company", tax_number: "", country: country || "NZ" });
            onCreated?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-entity-btn"><Plus className="h-4 w-4" /> Add entity</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-entity-dialog">
                <DialogHeader><DialogTitle>Add entity</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div><Label>Name</Label><Input className="mt-1" value={form.name} onChange={(e) => s("name", e.target.value)} data-testid="ae-name" /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Type</Label>
                            <Select value={form.entity_type} onValueChange={(v) => s("entity_type", v)}>
                                <SelectTrigger className="mt-1" data-testid="ae-type"><SelectValue /></SelectTrigger>
                                <SelectContent>{ENTITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div><Label>Tax number (IRD #)</Label><Input className="mt-1" value={form.tax_number} onChange={(e) => s("tax_number", e.target.value)} data-testid="ae-tax" /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} data-testid="ae-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="ae-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}



function ComplianceTab({ clientId, compliance, onChanged }) {
    const [open, setOpen] = useState(null); // record id being reviewed
    const [busy, setBusy] = useState(false);
    const [notes, setNotes] = useState("");
    void clientId;

    const rec = compliance.find((c) => c.id === open);

    async function accept(id) {
        setBusy(true);
        try {
            await api.post(`/compliance/${id}/accept`, { notes: notes || null });
            toast.success("Marked as accepted");
            setOpen(null); setNotes("");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    async function requestChanges(id) {
        if (!notes.trim()) { toast.error("Tell the client what to change"); return; }
        setBusy(true);
        try {
            await api.post(`/compliance/${id}/needs-changes`, { notes });
            toast.success("Sent back to client for changes");
            setOpen(null); setNotes("");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    if (!compliance.length) {
        return (
            <div className="co-card co-card-pad text-sm text-[#5E636E]" data-testid="compliance-empty">
                No compliance records yet. When the client opens their portal and fills out the year-end compliance form,
                a draft will appear here.
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="compliance-list">
            {compliance.map((c) => {
                const vehicles = c.motor_vehicle?.vehicles || [];
                return (
                    <div key={c.id} className="co-card co-card-pad" data-testid={`compliance-row-${c.id}`}>
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                                <div className="font-display text-lg font-medium">Tax year {c.tax_year}</div>
                                <div className="text-xs text-[#5E636E] mt-0.5">
                                    {c.submitted_at ? `Submitted ${relativeTime(c.submitted_at)}` : "Not yet submitted"}
                                    {c.declaration_name ? ` · ${c.declaration_name}` : ""}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <StatusBadge status={c.status} />
                                {c.status === "submitted" && (
                                    <Button size="sm" variant="outline" onClick={() => { setOpen(c.id); setNotes(""); }} data-testid={`compliance-review-${c.id}`}>Review</Button>
                                )}
                            </div>
                        </div>
                        <div className="mt-3 grid sm:grid-cols-2 gap-3 text-xs">
                            <div className="rounded-md border border-[#E4E4E7] p-3">
                                <div className="co-label">Home office</div>
                                {c.home_office?.has_home_office ? (
                                    <div className="mt-2 space-y-1">
                                        <div>Area: <strong>{c.home_office.office_area_sqm} / {c.home_office.total_home_area_sqm} m²</strong> ({c.home_office.business_use_percent}%)</div>
                                        <div>Dedicated: {c.home_office.is_dedicated_space ? "Yes" : "No"}</div>
                                        <div>Expenses: {(c.home_office.expense_categories || []).map((cat) => cat.replace(/_/g, " ")).join(", ") || "—"}</div>
                                    </div>
                                ) : (
                                    <div className="mt-2 text-[#5E636E]">Not claiming</div>
                                )}
                            </div>
                            <div className="rounded-md border border-[#E4E4E7] p-3">
                                <div className="co-label">Motor vehicle</div>
                                {c.motor_vehicle?.has_vehicle_claim ? (
                                    <div className="mt-2 space-y-1">
                                        {vehicles.map((v, i) => (
                                            <div key={i}>
                                                <strong>{v.registration || "—"}</strong> {v.make} {v.model} · {v.business_use_percent}% business · {v.method}
                                                {Number(v.business_use_percent) > 25 && (
                                                    <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800 px-1.5 py-0.5 text-[9px]">logbook required</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="mt-2 text-[#5E636E]">Not claiming</div>
                                )}
                            </div>
                        </div>
                        {c.reviewer_notes && (
                            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                <strong>Notes to client:</strong> {c.reviewer_notes}
                            </div>
                        )}
                    </div>
                );
            })}

            <Dialog open={!!open} onOpenChange={(v) => { if (!v) { setOpen(null); setNotes(""); } }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Review compliance — {rec?.tax_year}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <p className="text-sm text-[#5E636E]">Accept the submission, or send it back to the client with notes.</p>
                        <Label className="co-label">Notes (required for &quot;needs changes&quot;)</Label>
                        <textarea className="w-full rounded-md border border-[#E4E4E7] p-2 text-sm" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="eg. Power amount looks high — please double-check" data-testid="compliance-review-notes" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" disabled={busy} onClick={() => requestChanges(open)} data-testid="compliance-needs-changes-btn">Needs changes</Button>
                        <Button className="bg-[#171717] hover:bg-[#2E2E2E] text-white" disabled={busy} onClick={() => accept(open)} data-testid="compliance-accept-btn">Accept</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function ReviewToggle({ client, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [val, setVal] = useState(!!client.requires_tax_review);
    useEffect(() => { setVal(!!client.requires_tax_review); }, [client.id, client.requires_tax_review]);

    async function toggle(next) {
        setBusy(true);
        setVal(next);
        try {
            await api.patch(`/clients/${client.id}`, { requires_tax_review: next });
            toast.success(next ? "Returns will land as 'Ready for review'" : "Returns will be filed automatically");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); setVal(!next); }
        finally { setBusy(false); }
    }

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <div className="co-label">Tax-return review</div>
                <div className="mt-2 text-sm font-medium">Requires my review before filing</div>
                <p className="mt-1 text-xs text-[#5E636E] leading-relaxed max-w-md">
                    When ON, completed Practice Manager returns land in this client&apos;s portal as <span className="font-medium">Ready for review</span>. When OFF (default), they appear directly as <span className="font-medium">Filed with IRD</span> — no client approval needed.
                </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={val}
                    disabled={busy}
                    onChange={(e) => toggle(e.target.checked)}
                    data-testid="client-review-toggle"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:ring-2 peer-focus:ring-[#0E3B2E]/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E3B2E]"></div>
            </label>
        </div>
    );
}


function SpendInsightsToggle({ client, onChanged }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [busy, setBusy] = useState(false);
    const [val, setVal] = useState(!!client.spend_insights_enabled);
    useEffect(() => { setVal(!!client.spend_insights_enabled); }, [client.id, client.spend_insights_enabled]);

    async function toggle(next) {
        if (!isAdmin) { toast.error("Admin only"); return; }
        setBusy(true);
        setVal(next);
        try {
            await api.patch(`/spend-insights/clients/${client.id}/access`, { enabled: next });
            toast.success(next
                ? "Client will now see the 'Where your money went' tile on their portal home"
                : "Cash-out breakdown hidden from this client's portal");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); setVal(!next); }
        finally { setBusy(false); }
    }

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <div className="co-label">Cash-out insights (add-on)</div>
                <div className="mt-2 text-sm font-medium">Show &quot;Where your money went&quot; on the portal</div>
                <p className="mt-1 text-xs text-[#5E636E] leading-relaxed max-w-md">
                    Turns on the P&amp;L category breakdown tile + full <span className="font-mono">/portal/cash-out</span> page for this client. Uses the accounting data your sync workers already pull (Xero or Prosaic). Default OFF so books get sanity-checked first.
                </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0" title={isAdmin ? "" : "Admin only"}>
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={val}
                    disabled={busy || !isAdmin}
                    onChange={(e) => toggle(e.target.checked)}
                    data-testid="client-spend-insights-toggle"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:ring-2 peer-focus:ring-[#0E3B2E]/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E3B2E]"></div>
            </label>
        </div>
    );
}


function JobStatusOdometerToggle({ client, onChanged }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [busy, setBusy] = useState(false);
    const [val, setVal] = useState(!!client.job_status_odometer_enabled);
    useEffect(() => { setVal(!!client.job_status_odometer_enabled); }, [client.id, client.job_status_odometer_enabled]);

    async function toggle(next) {
        if (!isAdmin) { toast.error("Admin only"); return; }
        setBusy(true);
        setVal(next);
        try {
            await api.patch(`/job-status/clients/${client.id}/access`, { enabled: next });
            toast.success(next
                ? "Client will now see the job status dials on their portal home"
                : "Job status dials hidden from this client's portal");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); setVal(!next); }
        finally { setBusy(false); }
    }

    return (
        <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
                <div className="co-label">Job status odometer (add-on)</div>
                <div className="mt-2 text-sm font-medium">Show &quot;Your jobs right now&quot; on the portal</div>
                <p className="mt-1 text-xs text-[#5E636E] leading-relaxed max-w-md">
                    Adds a three-dial tile beneath Morning Brief showing what&apos;s in progress, waiting on the client, and completed in the last 90 days. Counts come from the Production Line (work items) plus open portal requests — no extra sync required.
                </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0" title={isAdmin ? "" : "Admin only"}>
                <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={val}
                    disabled={busy || !isAdmin}
                    onChange={(e) => toggle(e.target.checked)}
                    data-testid="client-job-status-odometer-toggle"
                />
                <div className="w-11 h-6 bg-zinc-200 peer-focus:ring-2 peer-focus:ring-[#0E3B2E]/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#0E3B2E]"></div>
            </label>
        </div>
    );
}


function ReadinessCard({ readiness, workItems, requests, submissions }) {
    const colour = readiness?.colour || "yellow";
    const score = readiness?.score ?? 0;
    const tone = colour === "green" ? "bg-emerald-500" : colour === "yellow" ? "bg-amber-500" : "bg-rose-500";
    const ring = colour === "green" ? "ring-emerald-200" : colour === "yellow" ? "ring-amber-200" : "ring-rose-200";
    const openWork = (workItems || []).filter((w) => !w.closed_at);
    const stageCounts = openWork.reduce((acc, w) => { acc[w.stage] = (acc[w.stage] || 0) + 1; return acc; }, {});
    const missingDocs = (requests || []).filter((r) => r.status !== "completed" && r.status !== "cancelled").flatMap((r) => r.doc_types_required || []);
    const reviewQueue = (submissions || []).filter((s) => s.status === "pending_review");

    return (
        <div className="co-card co-card-pad" data-testid="readiness-card">
            <div className="flex items-start justify-between">
                <div>
                    <div className="co-label">Client readiness</div>
                    <div className="mt-1 font-display text-3xl tabular-nums">{readiness ? `${score}/100` : "—"}</div>
                    <div className="text-xs text-[#5E636E] mt-0.5">{readiness ? (colour === "green" ? "Ready to file" : colour === "yellow" ? "Almost there" : "Blocked / needs attention") : "Loading…"}</div>
                </div>
                <span className={`h-10 w-10 rounded-full ${tone} ring-4 ${ring}`}></span>
            </div>
            {readiness?.factors && (
                <ul className="mt-3 space-y-1.5">
                    {readiness.factors.map((f) => (
                        <li key={f.key} className="flex items-center gap-2 text-[12px]">
                            <span className={`h-1.5 w-1.5 rounded-full ${f.ok ? "bg-emerald-500" : "bg-rose-500"}`}></span>
                            <span className="flex-1 truncate">{f.label}</span>
                            <span className="text-[10px] text-[#5E636E] tabular-nums">{f.points}/{f.max} · {f.detail}</span>
                        </li>
                    ))}
                </ul>
            )}
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <SmallStat n={stageCounts.waiting_on_client || 0} l="Waiting" />
                <SmallStat n={reviewQueue.length} l="To review" />
                <SmallStat n={missingDocs.length} l="Missing docs" />
            </div>
        </div>
    );
}

function SmallStat({ n, l }) {
    return (
        <div className="rounded-md bg-[#FAFAFA] border border-[#E4E4E7] py-2">
            <div className="font-display text-lg tabular-nums">{n}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[#5E636E]">{l}</div>
        </div>
    );
}

function DataSourceCard({ client, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [val, setVal] = useState(client.data_source || "manual");
    useEffect(() => { setVal(client.data_source || "manual"); }, [client.id, client.data_source]);

    async function setDataSource(next) {
        setBusy(true);
        setVal(next);
        try {
            await api.patch(`/clients/${client.id}`, { data_source: next });
            toast.success(`Data source set to ${OPTIONS.find((o) => o.value === next)?.label}`);
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); setVal(client.data_source || "manual"); }
        finally { setBusy(false); }
    }
    async function verify() {
        setBusy(true);
        try {
            await api.post(`/portal/onboarding/verify/${client.id}`, { note: null });
            toast.success("Marked as verified");
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    const status = client.data_source_status || "not_started";
    return (
        <div>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="co-label">Data source</div>
                    <div className="mt-1 text-sm font-medium">How we see this client&apos;s numbers</div>
                    <p className="mt-1 text-xs text-[#5E636E] max-w-md leading-relaxed">
                        Pick the platform this client uses. On first portal login they&apos;ll be asked to either connect via Prosaic, invite us into Xero, or both — depending on this setting.
                    </p>
                </div>
                <StatusPill status={status} />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="client-data-source-picker">
                {OPTIONS.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        disabled={busy}
                        onClick={() => setDataSource(o.value)}
                        className={`rounded-md border p-3 text-left transition-colors ${val === o.value ? "border-[#0E3B2E] bg-[#0E3B2E]/5" : "border-[#E4E4E7] hover:border-[#0E3B2E]/40"}`}
                        data-testid={`client-data-source-${o.value}`}
                    >
                        <div className="text-sm font-medium">{o.label}</div>
                        <div className="mt-0.5 text-[10px] text-[#5E636E] leading-tight">{o.hint}</div>
                    </button>
                ))}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="rounded-md bg-[#FAFAFA] border border-[#E4E4E7] px-3 py-2">
                    <div className="co-label">Prosaic confirmed</div>
                    <div className="mt-1">{client.prosaic_confirmed_at ? new Date(client.prosaic_confirmed_at).toLocaleDateString("en-NZ") : "—"}</div>
                </div>
                <div className="rounded-md bg-[#FAFAFA] border border-[#E4E4E7] px-3 py-2">
                    <div className="co-label">Xero confirmed</div>
                    <div className="mt-1">{client.xero_confirmed_at ? new Date(client.xero_confirmed_at).toLocaleDateString("en-NZ") : "—"}</div>
                </div>
            </div>

            {status === "client_confirmed" && (
                <Button onClick={verify} disabled={busy} className="mt-4 w-full sm:w-auto bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="client-data-source-verify">
                    Mark as verified
                </Button>
            )}
        </div>
    );
}

const OPTIONS = [
    { value: "prosaic", label: "Prosaic", hint: "Bank feeds via Prosaic" },
    { value: "xero", label: "Xero", hint: "Invited as adviser" },
    { value: "both", label: "Both", hint: "Bank feed + Xero access" },
    { value: "manual", label: "Manual upload only", hint: "Client uploads documents" },
];

function StatusPill({ status }) {
    const map = {
        not_started: { tone: "bg-amber-100 text-amber-800 border-amber-200", label: "Awaiting client" },
        client_confirmed: { tone: "bg-sky-100 text-sky-800 border-sky-200", label: "Pending verification" },
        verified: { tone: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Verified" },
    };
    const m = map[status] || map.not_started;
    return <span className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${m.tone}`}>{m.label}</span>;
}



// ---------------- Archive + Hard Delete ----------------
function DangerZone({ client, isAdmin, onArchived, onDeleted }) {
    const [busy, setBusy] = useState(false);
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [confirmName, setConfirmName] = useState("");
    const [ack, setAck] = useState(false);

    async function archive() {
        setBusy(true);
        try {
            await api.post(`/clients/${client.id}/archive`);
            toast.success(`${client.name} archived — hidden from active lists but every record is kept.`);
            setArchiveOpen(false);
            onArchived?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    async function unarchive() {
        setBusy(true);
        try {
            await api.post(`/clients/${client.id}/unarchive`);
            toast.success("Client restored");
            onArchived?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }
    async function hardDelete() {
        if (confirmName.trim() !== client.name.trim()) {
            toast.error("Client name doesn't match exactly");
            return;
        }
        if (!ack) { toast.error("Please tick the acknowledgement"); return; }
        setBusy(true);
        try {
            const { data } = await api.delete(`/clients/${client.id}`, {
                data: { confirm_name: confirmName, acknowledge_purge: true },
            });
            const parts = Object.entries(data.deleted || {}).map(([k, v]) => `${k}:${v}`).join(", ");
            toast.success(`Deleted ${client.name} · purged ${parts || "0 rows"}`);
            setDeleteOpen(false);
            onDeleted?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    }

    return (
        <>
            {client.archived_at ? (
                <Button variant="outline" size="sm" onClick={unarchive} disabled={busy} data-testid="client-unarchive-btn">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArchiveRestore className="h-3.5 w-3.5" />} Restore client
                </Button>
            ) : (
                <>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setArchiveOpen(true)}
                        disabled={busy}
                        className="border-amber-300 text-amber-800 hover:bg-amber-50 hover:text-amber-900"
                        title="Hide from active lists — every record stays for IRD retention"
                        data-testid="client-archive-btn"
                    >
                        <Archive className="h-3.5 w-3.5" /> Archive
                    </Button>
                    <Dialog open={archiveOpen} onOpenChange={setArchiveOpen}>
                        <DialogContent className="max-w-md" data-testid="client-archive-dialog">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2 text-amber-800">
                                    <Archive className="h-4 w-4" /> Archive {client.name}?
                                </DialogTitle>
                                <DialogDescription>
                                    Use this for former clients you no longer act for. They&apos;re hidden from active
                                    lists but every historical record is preserved for IRD retention. You can restore them anytime.
                                </DialogDescription>
                            </DialogHeader>
                            <ul className="text-sm text-[#3F444C] space-y-1.5 pl-4 list-disc">
                                <li>Removed from the Clients list and work-queue</li>
                                <li>Portal login is disabled (they can&apos;t sign in)</li>
                                <li>All documents, returns, requests &amp; timeline are kept intact</li>
                                <li>Xero / Prosaic sync stops for this entity</li>
                            </ul>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setArchiveOpen(false)} disabled={busy}>Cancel</Button>
                                <Button
                                    onClick={archive}
                                    disabled={busy}
                                    className="bg-amber-600 hover:bg-amber-700 text-white"
                                    data-testid="client-archive-confirm-btn"
                                >
                                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="h-4 w-4" /> Archive client</>}
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </>
            )}
            {isAdmin && (
                <Dialog open={deleteOpen} onOpenChange={(v) => { setDeleteOpen(v); if (!v) { setConfirmName(""); setAck(false); } }}>
                    <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-rose-700 border-rose-300 hover:bg-rose-50 hover:text-rose-800" data-testid="client-delete-btn">
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg" data-testid="client-delete-dialog">
                        <DialogHeader>
                            <DialogTitle className="text-rose-700">Permanently delete {client.name}?</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3 text-sm">
                            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-rose-900">
                                <div className="font-medium">This is irreversible.</div>
                                <div className="mt-1 text-xs">
                                    Purges the client and every linked record: documents, submissions, requests, tasks, tax summaries, tax audit log, cash ledger + day closes, morning briefs, health scores, financials, Xero/Prosaic connections, portal users linked only to this client, and activity trail.
                                    <br />Use <strong>Archive</strong> instead if you might need this history for IRD.
                                </div>
                            </div>
                            <div>
                                <Label className="co-label">Type the client name to confirm</Label>
                                <Input
                                    value={confirmName}
                                    onChange={(e) => setConfirmName(e.target.value)}
                                    placeholder={client.name}
                                    autoFocus
                                    data-testid="client-delete-confirm-name"
                                />
                            </div>
                            <label className="flex items-start gap-2 text-xs text-[#5E636E] cursor-pointer">
                                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="client-delete-ack" />
                                I understand this cannot be undone and I have exported anything I need for IRD retention.
                            </label>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={busy}>Cancel</Button>
                            <Button
                                className="bg-rose-600 hover:bg-rose-700 text-white"
                                onClick={hardDelete}
                                disabled={busy || confirmName.trim() !== client.name.trim() || !ack}
                                data-testid="client-delete-submit"
                            >
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Permanently delete
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

